import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { useEffect, useState } from "react";
import { fetchQuestionnaireStructure } from "../../Operator/OperatorAPI/OperatorGet";
import { postSetOverrideNext } from "../../Operator/OperatorAPI/OperatorPost";

const UserJumpDialog = ({ onClose, user }) => {
  const [structure, setStructure] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [confirm, setConfirm] = useState(null); // { label, pos }
  const [sending, setSending] = useState(false);
  // Move now by default: waiting for the test-taker to answer and press "next" is what
  // made moves feel like they hadn't worked.
  const [immediate, setImmediate] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await fetchQuestionnaireStructure(user.questionnaire);
        setStructure(data);
        // Default-expand the test the user is currently in
        if (data?.tests?.length) {
          const idx = data.tests.findIndex(t => t.testName === user.questionnaire_test);
          setExpanded(idx >= 0 ? idx : 0);
        }
      } catch (err) {
        setError("Failed to load questionnaire structure");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user.id, user.questionnaire, user.questionnaire_test]);

  const isCurrentChapter = (testName, chapterName) =>
    testName === user.questionnaire_test && chapterName === user.questionnaire_chapter;

  const isCurrentTest = (testName) => testName === user.questionnaire_test;

  // Build the first valid position for a chapter
  const chapterFirstPos = (testName, chapter) =>
    chapter.hasReferencesPage
      ? { state: "REFERENCES", testId: testName, chapterId: chapter.chapterName, questionIndex: null }
      : { state: "QUESTION", testId: testName, chapterId: chapter.chapterName, questionIndex: 0 };

  const handleJumpClick = (label, pos) => {
    setConfirm({ label, pos });
  };

  const handleConfirm = async () => {
    setSending(true);
    const result = await postSetOverrideNext(user.id, confirm.pos, immediate);
    setSending(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setConfirm(null);
    onClose();
  };

  return (
    <>
      <Dialog open={true} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          <Box>
            <Typography variant="h6">קפיצה לפרק — {user.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              מיקום נוכחי: {user.questionnaire_test} / {user.questionnaire_chapter}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {error && (
            <Typography color="error" sx={{ p: 2 }}>{error}</Typography>
          )}
          {structure && structure.tests.map((test, tIdx) => {
            const isCurTest = isCurrentTest(test.testName);
            return (
              <Accordion
                key={test.testName}
                expanded={expanded === tIdx}
                onChange={() => setExpanded(expanded === tIdx ? null : tIdx)}
                disableGutters
                elevation={0}
                sx={{
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  "&:before": { display: "none" },
                  bgcolor: isCurTest ? "primary.50" : undefined,
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ px: 2, py: 0.5, minHeight: 48 }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, mr: 1 }}>
                    <Typography fontWeight={isCurTest ? "bold" : "normal"} color={isCurTest ? "primary.main" : "text.primary"}>
                      {test.testName}
                    </Typography>
                    {isCurTest && (
                      <Chip
                        label="נוכחי"
                        size="small"
                        color="primary"
                        variant="outlined"
                        icon={<LocationOnIcon sx={{ fontSize: "0.9rem !important" }} />}
                        sx={{ height: 20, fontSize: "0.7rem" }}
                      />
                    )}
                  </Stack>
                  <Tooltip title={`קפוץ לתחילת שאלון ${test.testName}`}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      onClick={(e) => {
                        e.stopPropagation();
                        const firstChapter = test.chapters[0];
                        if (!firstChapter) return;
                        handleJumpClick(
                          `תחילת שאלון "${test.testName}"`,
                          chapterFirstPos(test.testName, firstChapter)
                        );
                      }}
                      sx={{ mr: 1, whiteSpace: "nowrap", flexShrink: 0 }}
                    >
                      ⏭ תחילת שאלון
                    </Button>
                  </Tooltip>
                </AccordionSummary>

                <AccordionDetails sx={{ px: 2, py: 1 }}>
                  <Stack spacing={0.5}>
                    {test.chapters.map((chapter, cIdx) => {
                      const isCurChapter = isCurrentChapter(test.testName, chapter.chapterName);
                      return (
                        <Box
                          key={chapter.chapterName}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            px: 1.5,
                            py: 0.75,
                            borderRadius: 1,
                            bgcolor: isCurChapter ? "primary.light" : cIdx % 2 === 0 ? "grey.50" : "background.paper",
                            border: isCurChapter ? "1px solid" : "1px solid transparent",
                            borderColor: isCurChapter ? "primary.main" : "transparent",
                          }}
                        >
                          <Stack direction="row" alignItems="center" spacing={1}>
                            {isCurChapter && <LocationOnIcon color="primary" sx={{ fontSize: "1rem" }} />}
                            <Typography
                              variant="body2"
                              fontWeight={isCurChapter ? "bold" : "normal"}
                              color={isCurChapter ? "primary.main" : "text.primary"}
                            >
                              {chapter.chapterName}
                            </Typography>
                            {chapter.hasReferencesPage && (
                              <Chip label="הפניות" size="small" sx={{ height: 16, fontSize: "0.65rem" }} />
                            )}
                          </Stack>
                          {/* The current chapter stays clickable: restarting it is the only
                              way back for someone stuck at its end with no time left. */}
                          <Button
                            size="small"
                            variant={isCurChapter ? "outlined" : "contained"}
                            color={isCurChapter ? "warning" : "primary"}
                            onClick={() =>
                              handleJumpClick(
                                `פרק "${chapter.chapterName}" (${test.testName})`,
                                chapterFirstPos(test.testName, chapter)
                              )
                            }
                          >
                            {isCurChapter ? "התחל פרק מחדש" : "קפוץ לכאן"}
                          </Button>
                        </Box>
                      );
                    })}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </DialogContent>

        <DialogActions sx={{ flexWrap: "wrap" }}>
          <FormControlLabel
            sx={{ flex: 1, ml: 1, mr: 0 }}
            control={
              <Checkbox
                size="small"
                checked={immediate}
                onChange={e => setImmediate(e.target.checked)}
              />
            }
            label={
              <Typography variant="caption" color="text.secondary">
                {immediate
                  ? "העבר מיד — הנבחן יקבל הודעה ויועבר בלי להמתין"
                  : 'המתן — המעבר יתרחש כשהנבחן ילחץ על "הבא"'}
              </Typography>
            }
          />
          <Button onClick={onClose}>סגור</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm dialog */}
      <Dialog open={!!confirm} onClose={() => !sending && setConfirm(null)} maxWidth="xs">
        <DialogTitle>אישור קפיצה</DialogTitle>
        <DialogContent>
          <DialogContentText dir="rtl">
            האם להזיז את <b>{user.name}</b> לתחילת <b>{confirm?.label}</b>?
            <br />
            <Typography component="span" variant="caption" color="text.secondary">
              {immediate
                ? "הנבחן יועבר מיד ותוצג לו הודעה על כך."
                : 'הנבחן יועבר כשילחץ על "הבא".'}
              <br />
              אם לא נותר לו זמן ביעד, יובטחו לו לפחות שתי דקות.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={sending}>ביטול</Button>
          <Button onClick={handleConfirm} variant="contained" color="warning" disabled={sending}>
            {sending ? <CircularProgress size={20} /> : "אשר הזזה"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserJumpDialog;
